/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import {
  FlexContainer,
  Tag,
  Text,
  theme,
  SVGIcon,
  ExtendedTooltip,
} from "@nordcloud/gnui";
import { ApiKey } from "~/generated/graphql";
import { DateCell } from "~/components/Tables/cells/DateCell";
import { NameWithLink } from "~/components/Tables/cells/NameWithLink";
import {
  ActionsColumn,
  FlexColumn,
  FlexColumnMax,
  MediumIconColumn,
  SmallColumn,
} from "~/components/Tables/styles";
import { TABLE_LABELS } from "~/constants";
import { Actions } from "./Actions";

const columnHelper = createColumnHelper<ApiKey>();

type Props = {
  refetchApiKeys: () => void;
};

export function apiKeysColumns({ refetchApiKeys }: Props) {
  return [
    columnHelper.accessor(() => null, {
      id: "isExpired",
      header: "",
      cell: ({ row }) => {
        const isExpiredProperties = getIsExpiredProperties(
          row.original.expiresAt
        );

        return (
          <FlexContainer justifyContent="center" alignItems="center">
            <ExtendedTooltip
              status={isExpiredProperties.colorStatus}
              caption={
                <Text tag="span" size="sm" color={theme.color.text.text04}>
                  {isExpiredProperties.caption}
                </Text>
              }
              position="start"
            >
              <FlexContainer justifyContent="center" alignItems="center">
                <SVGIcon name={isExpiredProperties.iconName} />
              </FlexContainer>
            </ExtendedTooltip>
          </FlexContainer>
        );
      },
      meta: MediumIconColumn,
    }),
    columnHelper.accessor((row) => row?.name, {
      header: "API Key Name",
      cell: ({ row }) => <NameWithLink name={row.original.name} />,
      meta: FlexColumn,
    }),
    columnHelper.accessor((row) => row?.description, {
      header: "Roles",
      cell: ({ row }) => (
        <div>
          {row.original.roleBindings?.result?.map((role) => (
            <div key={role?.role.id}>
              <Tag text={role?.role.name} />
            </div>
          ))}
        </div>
      ),
      meta: FlexColumnMax,
    }),
    columnHelper.accessor((row) => row?.changeDetails?.createdAt, {
      header: TABLE_LABELS.createdAt,
      cell: ({ row }) => (
        <DateCell value={row.original.changeDetails.createdAt} />
      ),
      meta: SmallColumn,
    }),
    columnHelper.accessor((row) => row?.lastUsedAt, {
      header: "Last Used At",
      cell: ({ row }) => <DateCell value={row.original.lastUsedAt} />,
      meta: SmallColumn,
    }),
    columnHelper.accessor((row) => row?.changeDetails?.updatedAt, {
      header: TABLE_LABELS.updatedAt,
      cell: ({ row }) => (
        <DateCell value={row.original.changeDetails.updatedAt} />
      ),
      meta: SmallColumn,
    }),
    columnHelper.accessor((row) => row?.expiresAt, {
      header: "Expires",
      cell: ({ row }) => <DateCell value={row?.original?.expiresAt} />,
      meta: SmallColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "options",
      header: () => null,
      cell: ({ row }) => {
        const id = row.original.id;
        const rolesBindings =
          row.original.roleBindings?.result?.map((role) => role?.id ?? "") ??
          [];
        return (
          <FlexContainer justifyContent="space-around">
            <Actions
              ids={[id]}
              apiKey={row.original}
              rolesIds={rolesBindings}
              refetchApiKeys={refetchApiKeys}
            />
          </FlexContainer>
        );
      },
      meta: ActionsColumn,
    }),
  ] as never;
}

type IsExpiredProperties = {
  colorStatus: "success" | "danger";
  caption: string;
  iconName: "statusSuccess" | "statusError";
};

const getIsExpiredProperties = (
  expiresAt: string | undefined | null
): IsExpiredProperties => {
  const isExpired = dayjs(expiresAt).diff(dayjs(), "minute") <= 0;

  if (!isExpired) {
    return {
      colorStatus: "success",
      caption: "Active",
      iconName: "statusSuccess",
    };
  }

  return { colorStatus: "danger", caption: "Expired", iconName: "statusError" };
};
